import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useEffect } from "react";
import "@/App.css";
import LandingPage from "@/pages/public/LandingPage";
import CandidateJobsPage from "@/pages/candidate/CandidateJobsPage";
import CandidateJobDetailPage from "@/pages/candidate/CandidateJobDetailPage";
import CandidateInterviewPage from "@/pages/candidate/CandidateInterviewPage";
import PostInterviewPage from "@/pages/candidate/PostInterviewPage";
import CandidateLoginPage from "@/pages/candidate/CandidateLoginPage";
import CandidateVerifyPage from "@/pages/candidate/CandidateVerifyPage";
import CandidateDashboardPage from "@/pages/candidate/CandidateDashboardPage";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminJobsPage from "@/pages/admin/AdminJobsPage";
import JobWorkflowConfigPage from "@/pages/admin/JobWorkflowConfigPage";
import AdminSessionsPage from "@/pages/admin/AdminSessionsPage";
import AdminReportPage from "@/pages/admin/AdminReportPage";
import AdminTestsPage from "@/pages/admin/AdminTestsPage";
import AITestGeneratorPage from "@/pages/admin/AITestGeneratorPage";
import AdminTestBuilderPage from "@/pages/admin/AdminTestBuilderPage";
import AdminTestPreviewPage from "@/pages/admin/AdminTestPreviewPage";
import AdminTestCandidatesPage from "@/pages/admin/AdminTestCandidatesPage";
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminSettingsPage from "@/pages/admin/AdminSettingsPage";
import AdminSystemSettingsPage from "@/pages/admin/AdminSystemSettingsPage";
import AdminProctorLivePage from "@/pages/admin/AdminProctorLivePage";
import PublicTestPage from "@/pages/public/PublicTestPage";
import PublicJobApplicationPage from "@/pages/public/PublicJobApplicationPage";
import CandidateTakeTestPage from "@/pages/candidate/CandidateTakeTestPage";
import PanelAuthPage from "@/pages/panel/PanelAuthPage";
import HiringManagerPortalPage from "@/pages/hiring-manager/HiringManagerPortalPage";
import PracticeModePage from "@/pages/public/PracticeModePage";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { CandidateAuthProvider } from "@/contexts/CandidateAuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ProtectedCandidateRoute } from "@/components/ProtectedCandidateRoute";

function App() {
  // Remove Emergent badge aggressively
  useEffect(() => {
    const removeBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) badge.remove();
      document.querySelectorAll('a[href*="emergent"]').forEach(el => el.remove());
    };
    
    removeBadge();
    const interval = setInterval(removeBadge, 500);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <ThemeProvider>
      <div className="min-h-screen bg-background text-foreground transition-colors duration-300">
      <BrowserRouter>
        <AuthProvider>
          <CandidateAuthProvider>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              
              {/* Public job application */}
              <Route path="/jobs/:jobId/apply" element={<PublicJobApplicationPage />} />

              
              {/* Candidate Authentication */}
              <Route path="/candidate/login" element={<CandidateLoginPage />} />
              <Route path="/candidate/verify" element={<CandidateVerifyPage />} />
              
              {/* Candidate Portal - PROTECTED */}
              <Route path="/candidate/dashboard" element={<ProtectedCandidateRoute><CandidateDashboardPage /></ProtectedCandidateRoute>} />
              
              {/* Legacy Candidate routes - PUBLIC (Keep for backward compatibility) */}
              <Route path="/jobs" element={<CandidateJobsPage />} />
              <Route path="/jobs/:jobId" element={<CandidateJobDetailPage />} />
              <Route path="/interview/:sessionId" element={<CandidateInterviewPage />} />
              <Route path="/interview/:sessionId/complete" element={<PostInterviewPage />} />
              
              {/* Candidate test-taking (via invite link) - PUBLIC */}
              <Route path="/take-test/:inviteToken" element={<CandidateTakeTestPage />} />

              {/* Panel member magic-link landing */}
              <Route path="/panel/auth/:token" element={<PanelAuthPage />} />
              
              {/* Public test access (shareable link) - PUBLIC */}
              <Route path="/test/:testId" element={<PublicTestPage />} />

              {/* Practice Mode - PUBLIC (no auth, no record kept) */}
              <Route path="/practice/:category" element={<PracticeModePage />} />

              {/* Hiring Manager portal - PUBLIC (activated by magic-link token) */}
              <Route path="/hiring-manager/auth/:token" element={<HiringManagerPortalPage />} />
              
              {/* Admin login - PUBLIC */}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              
              {/* Admin routes - PROTECTED */}
              <Route path="/admin" element={<ProtectedRoute><AdminDashboardPage /></ProtectedRoute>} />
              <Route path="/admin/jobs" element={<ProtectedRoute><AdminJobsPage /></ProtectedRoute>} />
              <Route path="/admin/jobs/:jobId/workflow" element={<ProtectedRoute><JobWorkflowConfigPage /></ProtectedRoute>} />
              <Route path="/admin/sessions" element={<ProtectedRoute><AdminSessionsPage /></ProtectedRoute>} />
              <Route path="/admin/reports/:sessionId" element={<ProtectedRoute><AdminReportPage /></ProtectedRoute>} />
              <Route path="/admin/settings" element={<ProtectedRoute requireSuperAdmin><AdminSettingsPage /></ProtectedRoute>} />
              <Route path="/admin/system-settings" element={<ProtectedRoute requireSuperAdmin><AdminSystemSettingsPage /></ProtectedRoute>} />
              <Route path="/admin/proctor-live" element={<ProtectedRoute><AdminProctorLivePage /></ProtectedRoute>} />
              
              {/* Screening Tests - PROTECTED */}
              <Route path="/admin/tests" element={<ProtectedRoute><AdminTestsPage /></ProtectedRoute>} />
              <Route path="/admin/tests/ai-generate" element={<ProtectedRoute><AITestGeneratorPage /></ProtectedRoute>} />
              <Route path="/admin/tests/new" element={<ProtectedRoute><AdminTestBuilderPage /></ProtectedRoute>} />
              <Route path="/admin/tests/:testId/edit" element={<ProtectedRoute><AdminTestBuilderPage /></ProtectedRoute>} />
              <Route path="/admin/tests/:testId/preview" element={<ProtectedRoute><AdminTestPreviewPage /></ProtectedRoute>} />
              <Route path="/admin/tests/:testId/candidates" element={<ProtectedRoute><AdminTestCandidatesPage /></ProtectedRoute>} />
            </Routes>
          </CandidateAuthProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </div>
    </ThemeProvider>
  );
}

export default App;
